import { useApi } from '@shared/services/api';
import { useCallback, useContext, useEffect, useState } from 'react';

import { UserContext, UserLoadState } from '@/shared/contexts/UserContext';
import type { CuratedAccountPlanQuestion } from '../types.ts';

interface ApiEnvelope<T> {
  success?: boolean;
  data?: T;
  error?: boolean | string | { message?: string };
  message?: string;
}

interface UseNetworkQuestionsResult {
  questions: CuratedAccountPlanQuestion[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  submitAnswer: (questionId: number, answer: string) => Promise<boolean>;
}

/**
 *
 * @param res
 */
function envelopeError(res: ApiEnvelope<unknown>): string | null {
  if (!res.error) return null;
  if (typeof res.error === 'string') return res.error;
  if (typeof res.error === 'object' && res.error !== null && 'message' in res.error) {
    return res.error.message ?? null;
  }
  return res.message ?? 'Request failed';
}

/**
 *
 */
export function useNetworkQuestions(): UseNetworkQuestionsResult {
  const api = useApi();
  const { userLoadState } = useContext(UserContext);
  const isAuthenticated = userLoadState === UserLoadState.Authenticated;
  const [questions, setQuestions] = useState<CuratedAccountPlanQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchQuestions = useCallback(async (): Promise<void> => {
    if (!isAuthenticated) {
      setQuestions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    const res = await api.get<ApiEnvelope<CuratedAccountPlanQuestion[]>>('account-plan-question/curated');
    const errorMessage = envelopeError(res);
    if (errorMessage) {
      setError(errorMessage);
      setQuestions([]);
    } else {
      setQuestions(res.data ?? []);
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  useEffect(() => {
    fetchQuestions();
  }, [fetchQuestions]);

  // Refetch when the tab becomes visible again so newly-asked questions
  // appear without the user having to reload. Event-driven, not polling.
  useEffect(() => {
    if (!isAuthenticated) return;
    const onVisible = (): void => {
      if (document.visibilityState === 'visible') {
        fetchQuestions();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return (): void => {
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated, fetchQuestions]);

  const submitAnswer = useCallback(async (questionId: number, answer: string): Promise<boolean> => {
    const trimmed = answer.trim();
    if (trimmed.length === 0) return false;

    const res = await api.post<ApiEnvelope<unknown>>(`account-plan-question/${questionId}/answer`, { answer: trimmed });
    const errorMessage = envelopeError(res);
    if (errorMessage) {
      setError(errorMessage);
      return false;
    }
    setQuestions((previous) => {
      return previous.filter((q) => {
        return q.id !== questionId;
      });
    });
    return true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    questions,
    loading,
    error,
    refresh: fetchQuestions,
    submitAnswer,
  };
}
